import { useEffect, useState } from 'preact/hooks'
import type { Dispatch, StateUpdater } from 'preact/hooks'
import { websApi, type WebViewDto } from '@/api/webs'

interface UseSpeakerImportParams {
    web: WebViewDto | null
    hasBigMarker: boolean
    /** The import endpoint is editor-only; a viewer's call would 403 on every page load (PR 203 review). */
    canEdit: boolean
    setWeb: Dispatch<StateUpdater<WebViewDto | null>>
}

/**
 * Whether this web still owes its one-time import of the BigMarker conference's presenters (ORB-942).
 * Mirrors the server's rule, so the call is only made when it will do something. Not gated on
 * sign-off: the import is additive, and an import cut short by a sign-off must still finish.
 */
export function speakerImportOwed(web: WebViewDto | null, hasBigMarker: boolean): boolean {
    const evt = web?.OnlineEvent
    return !!evt?.ConferenceId
        && hasBigMarker
        && !evt.LastConferenceSyncUtc
        && !evt.ConferenceNotFound
}

/**
 * Imports the conference's presenters as speakers, once, after a Conference Id is set (ORB-942).
 * Replaces the sync that pulled title, overview and speakers from BigMarker on every page load: the
 * platform now owns those, and this is the only read left.
 */
export function useSpeakerImport({ web, hasBigMarker, canEdit, setWeb }: UseSpeakerImportParams): { importingSpeakers: boolean } {
    const [importingSpeakers, setImportingSpeakers] = useState(false)
    // Only an editor runs it; the next editor to open the page imports for everyone.
    const owed = canEdit && speakerImportOwed(web, hasBigMarker)

    useEffect(() => {
        // A request cleaned up because the import stopped being owed never reaches its own reset, so
        // clear the state here or the spinner and speaker lock would outlive it.
        if (!web || !owed) {
            setImportingSpeakers(false)
            return
        }

        let active = true
        const { WorkspaceId, Id } = web

        ;(async () => {
            setImportingSpeakers(true)
            try {
                const res = await websApi.importSpeakers(WorkspaceId, Id)
                if (!active) return
                if (res.data) setWeb(res.data)
            } finally {
                if (active) setImportingSpeakers(false)
            }
        })()

        return () => { active = false }
    }, [web?.Id, web?.WorkspaceId, web?.OnlineEvent?.ConferenceId, owed])

    return { importingSpeakers }
}
